import * as styled from './parent.styles';
import * as QuizStyled from './quiz.styles';
import * as FeaturedStyled from './featured.styles';
import { PRODUCT_CONFIG } from '../../config/product-data';
import ProductData from 'lib/product-data';
import { PRODUCT_SKUS } from 'lib/constants/product-skus';
import { trackButtonClicked } from 'lib/analytics';
import { useActiveMiniSale } from 'hooks/use-active-mini-sale';
import { useContext } from 'react';
import { CartContext } from 'context-providers/cart-provider';
import { ProductsContext } from 'context-providers/products-provider';
import { formatMoney } from 'lib/utils/format-money';
import {
  applyPromoCodeDiscount,
  getPromoSavingsLabel,
} from 'lib/utils/coupons/apply-promo-code-discount';
import { discountAppliesToProduct } from 'lib/utils/coupons/discount-applies-to-product';

const QuizCard = () => {
  return (
    <QuizStyled.Parent>
      <QuizStyled.Body>
        <QuizStyled.Text>
          Is DS-01® the right probiotic for you?{' '}
          <QuizStyled.Link href='/is-ds01-right-for-you'>
            Take the Quiz
          </QuizStyled.Link>
        </QuizStyled.Text>
        <QuizStyled.MobileImage
          src={QuizStyled.bgImg}
          alt='open hand holding green pills outside'
          fill
          priority
        />
      </QuizStyled.Body>
    </QuizStyled.Parent>
  );
};

const Featured = (featured) => {
  const { openCartFromUrl, promoCodeDiscountInfo } = useContext(CartContext);
  const { currencyCode, getProductPrice, getProduct } =
    useContext(ProductsContext);
  const config = PRODUCT_CONFIG[featured.productSku];
  const miniSale = useActiveMiniSale();
  const isDs01 = featured.productSku === PRODUCT_SKUS.DAILY_SYNBIOTIC;
  const isUsdMarket = currencyCode === 'USD';

  // USD shows the canonical plans-API price; other markets show the localized
  // variant amount, rendering no price rather than a mislabeled USD one.
  const displayPrice = isUsdMarket
    ? featured.price
    : getProductPrice(featured.productSku);

  const productId = getProduct(featured.productSku)?.shopifyData?.productID;
  const promoApplies =
    !!promoCodeDiscountInfo &&
    discountAppliesToProduct(productId, promoCodeDiscountInfo.items);
  const promoPrice = promoApplies
    ? applyPromoCodeDiscount(
        promoCodeDiscountInfo,
        Number(displayPrice),
        currencyCode,
      ).price
    : null;

  // The applied code is the only source of a displayed discount here — it's
  // what checkout charges, and it covers the sale's own code too.
  const salePrice =
    promoPrice != null && displayPrice && promoPrice < Number(displayPrice)
      ? promoPrice.toFixed(2)
      : null;

  // The code's savings label wins so the pill outlives the sale window and
  // reports the shopper's actual rate; the sale badge covers the gap before
  // the code resolves.
  const pillText =
    (salePrice && getPromoSavingsLabel(promoCodeDiscountInfo, currencyCode)) ||
    (miniSale && isDs01 ? miniSale.saleBadgeText : 'Bestseller');

  const priceContent =
    salePrice && displayPrice ? (
      <>
        {formatMoney(salePrice, currencyCode)}
        <FeaturedStyled.StrikePrice>
          {formatMoney(displayPrice, currencyCode)}
        </FeaturedStyled.StrikePrice>
      </>
    ) : displayPrice ? (
      <>{formatMoney(displayPrice, currencyCode)}</>
    ) : null;

  return (
    <FeaturedStyled.Container>
      <FeaturedStyled.Pill>{pillText}</FeaturedStyled.Pill>
      <FeaturedStyled.FeaturedImage
        src={featured.image}
        alt={featured.name}
        fill
        priority
      />
      <FeaturedStyled.Body>
        <FeaturedStyled.InnerText>
          <div style={{ color: 'inherit' }}>
            <FeaturedStyled.CodePill>
              {config?.alphaCode}
            </FeaturedStyled.CodePill>
            <FeaturedStyled.Name>{config?.displayName}</FeaturedStyled.Name>
          </div>
          <FeaturedStyled.MobilePrice>
            {priceContent}
          </FeaturedStyled.MobilePrice>
        </FeaturedStyled.InnerText>

        <FeaturedStyled.Desc>{config?.description}</FeaturedStyled.Desc>
        <FeaturedStyled.DesktopPrice>
          {priceContent}
        </FeaturedStyled.DesktopPrice>
        <FeaturedStyled.CTA>
          <FeaturedStyled.LearnMore
            id='learn-more-button'
            href={ProductData[featured.productSku].pdpUrl}
            onClick={() =>
              trackButtonClicked('/products - Learn More', featured.productSku)
            }
          >
            Learn More
          </FeaturedStyled.LearnMore>
          <FeaturedStyled.AddToCart
            id='add-to-cart-button'
            href={ProductData[featured.productSku].buyUrl}
            onClick={(event) => {
              event.preventDefault();
              trackButtonClicked(
                '/products - Add to Cart',
                featured.productSku,
              );
              openCartFromUrl(ProductData[featured.productSku].buyUrl);
            }}
          >
            Add To Cart
          </FeaturedStyled.AddToCart>
        </FeaturedStyled.CTA>
      </FeaturedStyled.Body>
    </FeaturedStyled.Container>
  );
};

const FeaturedProduct = ({ featured }) => (
  <styled.Container>
    <styled.Body>
      <Featured {...featured} />
      <QuizCard />
    </styled.Body>
  </styled.Container>
);

export default FeaturedProduct;
